// Fractional ranks (base-62 strings, ordered by plain string comparison). The server is authoritative for
// final ranks; the client computes provisional ranks so local order is stable offline. Same algorithm in PHP.
const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const BASE = DIGITS.length;

/** Returns a string strictly between a and b (null = open bound). Results never end with '0'. */
export function rankBetween(a: string | null, b: string | null): string {
  if (a !== null && b !== null && a >= b) throw new Error(`rankBetween: ${a} >= ${b}`);
  let out = '';
  let open = b === null;
  for (let i = 0; ; i++) {
    const lo = a !== null && i < a.length ? DIGITS.indexOf(a[i]!) : 0;
    const hi = !open && i < b!.length ? DIGITS.indexOf(b![i]!) : BASE;
    if (lo === hi) { out += DIGITS[lo]; continue; }
    const mid = (lo + hi) >> 1;
    if (mid > lo) return out + DIGITS[mid];
    out += DIGITS[lo];
    open = true;
  }
}

/** n increasing, evenly spaced ranks of fixed width (bulk seeding). */
export function initialRanks(n: number): string[] {
  let width = 1;
  while (Math.pow(BASE, width) < (n + 1) * 4) width++;
  const space = Math.pow(BASE, width), step = Math.floor(space / (n + 1));
  const out: string[] = [];
  for (let i = 1; i <= n; i++) {
    let v = i * step, s = '';
    for (let k = 0; k < width; k++) { s = DIGITS[v % BASE] + s; v = Math.floor(v / BASE); }
    out.push(s.replace(/0+$/, '') || '1');
  }
  return out;
}

/**
 * Assigns ranks to an ordered sequence where `stable` items keep their (increasing) rank and every other
 * item gets a rank between its nearest stable neighbours. Returns ranks aligned with `items`.
 */
export function assignRanks(items: Array<{ rank?: string | null; stable: boolean }>): string[] {
  const out: string[] = new Array(items.length);
  let prev: string | null = null;
  for (let i = 0; i < items.length; i++) {
    const it = items[i]!;
    if (it.stable && it.rank && (prev === null || it.rank > prev)) { out[i] = it.rank; prev = it.rank; continue; }
    let next: string | null = null;
    for (let j = i + 1; j < items.length; j++) { const n = items[j]!; if (n.stable && n.rank && (prev === null || n.rank > prev)) { next = n.rank; break; } }
    prev = rankBetween(prev, next);
    out[i] = prev;
  }
  return out;
}

/** Indices (into `seq`) of a longest increasing subsequence — items that did not move relative to each other. */
export function lisIndices(seq: number[]): Set<number> {
  const tails: number[] = [], prevIdx: number[] = new Array(seq.length).fill(-1), tailIdx: number[] = [];
  for (let i = 0; i < seq.length; i++) {
    let lo = 0, hi = tails.length;
    while (lo < hi) { const m = (lo + hi) >> 1; if (tails[m]! < seq[i]!) lo = m + 1; else hi = m; }
    tails[lo] = seq[i]!; tailIdx[lo] = i; prevIdx[i] = lo > 0 ? tailIdx[lo - 1]! : -1;
  }
  const res = new Set<number>();
  let k = tails.length ? tailIdx[tails.length - 1]! : -1;
  while (k >= 0) { res.add(k); k = prevIdx[k]!; }
  return res;
}
